import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { ProductSpecificationFilterGroup, ProductSpecificationFilterOption } from 'situview-webapi-client';
import { ProductService } from '../../services/product.service';

interface FilterNode {
  spec: ProductSpecificationFilterOption;
  children: FilterNode[];
  parent?: FilterNode;
  depth: number;
}

@Component({
  selector: 'product-specification-filter',
  templateUrl: './product-specification-filter.component.html',
  styleUrls: ['./product-specification-filter.component.scss']
})
export class ProductSpecificationFilterComponent implements OnChanges {
  @Input() productTypeId: string | undefined;
  @Input() selectedSpecificationIds: string[] = [];
  @Output() filterGroupsChange = new EventEmitter<ProductSpecificationFilterGroup[]>();
  @Output() selectedSpecificationIdsChange = new EventEmitter<string[]>();

  isOpen = false;
  loading = false;
  rootNodes: FilterNode[] = [];
  overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
  ];
  private nodesById = new Map<string, FilterNode>();
  private tickedIds = new Set<string>();

  constructor(private productService: ProductService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['productTypeId']) {
      this.tickedIds.clear();
      this.rootNodes = [];
      this.nodesById.clear();
      if (this.productTypeId) {
        this.loadOptions(this.productTypeId);
      }
    }
    if (changes['selectedSpecificationIds']) {
      this.tickedIds = new Set(this.selectedSpecificationIds ?? []);
      // If seeded externally (non-empty IDs) and tree is already loaded, emit
      // filterGroupsChange so the parent applies the filter to the search.
      // If the tree isn't loaded yet, emission happens at the end of buildTree.
      if (this.tickedIds.size > 0 && this.nodesById.size > 0) {
        this.emitFilterGroupsOnly();
      }
    }
  }

  private loadOptions(productTypeId: string) {
    this.loading = true;
    this.productService.LoadProductSpecificationFilterOptions(productTypeId)
      .then(options => {
        if (this.productTypeId !== productTypeId) return; // user changed type while loading
        this.buildTree(options);
        // Seed may have arrived before options were loaded — emit now that we
        // can resolve groups from the tree.
        if (this.tickedIds.size > 0) {
          this.emitFilterGroupsOnly();
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private emitFilterGroupsOnly() {
    // Used when the parent already owns the IDs (controlled input). We emit
    // only the filter-groups change so the search re-runs; we don't echo the
    // IDs back, as that would just round-trip the same value.
    this.filterGroupsChange.emit(this.buildFilterGroups());
  }

  private buildTree(options: ProductSpecificationFilterOption[]) {
    this.nodesById.clear();
    const ROOT_KEY = '__root__';
    const childrenByParent = new Map<string, FilterNode[]>();

    const sorted = [...options].sort((a, b) =>
      (a.order ?? 100000) - (b.order ?? 100000) ||
      (a.name ?? '').localeCompare(b.name ?? '')
    );

    sorted.forEach(spec => {
      // Defensive: skip any spec without a real id — would alias the root bucket
      // and create a cycle when we later look up children by id.
      if (!spec.id) return;
      const node: FilterNode = { spec, children: [], depth: 0 };
      this.nodesById.set(spec.id, node);
      const parentKey = spec.parentSpecificationId ? spec.parentSpecificationId : ROOT_KEY;
      const list = childrenByParent.get(parentKey) ?? [];
      list.push(node);
      childrenByParent.set(parentKey, list);
    });

    this.nodesById.forEach(node => {
      if (!node.spec.id) return;
      const kids = childrenByParent.get(node.spec.id) ?? [];
      kids.forEach(k => {
        if (k === node) return; // self-cycle guard
        k.parent = node;
      });
      node.children = kids;
    });

    this.rootNodes = childrenByParent.get(ROOT_KEY) ?? [];
    this.recomputeNodeDepths(this.rootNodes, 0, new Set());
  }

  private recomputeNodeDepths(nodes: FilterNode[], depth: number, visited: Set<FilterNode>) {
    nodes.forEach(n => {
      if (visited.has(n)) return;
      visited.add(n);
      n.depth = depth;
      this.recomputeNodeDepths(n.children, depth + 1, visited);
    });
  }

  toggleOpen() {
    if (!this.productTypeId) return;
    this.isOpen = !this.isOpen;
  }

  closePanel() {
    this.isOpen = false;
  }

  isTicked(id: string | undefined): boolean {
    return !!id && this.tickedIds.has(id);
  }

  onTick(node: FilterNode, checked: boolean) {
    if (!node.spec.id) return;
    if (checked) {
      this.tickedIds.add(node.spec.id);
    } else {
      this.tickedIds.delete(node.spec.id);
    }
    this.emitChanges();
  }

  removeChip(specId: string) {
    // The chip represents either a directly-ticked node, or a leaf descendant
    // of a ticked parent. If the user removes a leaf chip that came from a
    // parent tick, untick the parent and re-tick the sibling leaves so the
    // remaining chips stay accurate.
    const node = this.nodesById.get(specId);
    if (!node) return;

    if (this.tickedIds.has(specId)) {
      this.tickedIds.delete(specId);
    } else {
      // walk up to find the ticked ancestor
      let ancestor = node.parent;
      while (ancestor && !this.tickedIds.has(ancestor.spec.id ?? '')) {
        ancestor = ancestor.parent;
      }
      if (ancestor && ancestor.spec.id) {
        this.tickedIds.delete(ancestor.spec.id);
        // promote ancestor's leaves (other than the removed one) to direct ticks
        const leaves = this.collectLeafIds(ancestor);
        leaves.forEach(id => {
          if (id !== specId) this.tickedIds.add(id);
        });
      }
    }
    this.emitChanges();
  }

  clearAll() {
    if (this.tickedIds.size === 0) return;
    this.tickedIds.clear();
    this.emitChanges();
  }

  private emitChanges() {
    this.selectedSpecificationIdsChange.emit(Array.from(this.tickedIds));
    this.filterGroupsChange.emit(this.buildFilterGroups());
  }

  private buildFilterGroups(): ProductSpecificationFilterGroup[] {
    // Group key rule:
    // - tick on a node WITH children (a section header itself): group key = node.spec.id,
    //   specIds = self + all descendant ids
    // - tick on a leaf: group key = node.spec.parentSpecificationId (null for top-level),
    //   specIds includes this leaf
    const groupsByKey = new Map<string, Set<string>>();
    const keyFor = (id: string | null | undefined) => id ?? '__top__';

    this.tickedIds.forEach(tickedId => {
      const node = this.nodesById.get(tickedId);
      if (!node || !node.spec.id) return;

      if (node.children.length > 0) {
        const groupKey = keyFor(node.spec.id);
        const set = groupsByKey.get(groupKey) ?? new Set<string>();
        this.collectAllIds(node).forEach(id => set.add(id));
        groupsByKey.set(groupKey, set);
      } else {
        const groupKey = keyFor(node.spec.parentSpecificationId);
        const set = groupsByKey.get(groupKey) ?? new Set<string>();
        set.add(node.spec.id);
        groupsByKey.set(groupKey, set);
      }
    });

    const groups: ProductSpecificationFilterGroup[] = [];
    groupsByKey.forEach((ids, key) => {
      const group = new ProductSpecificationFilterGroup();
      group.parentSpecificationId = key === '__top__' ? undefined : key;
      group.specificationIds = Array.from(ids);
      groups.push(group);
    });
    return groups;
  }

  private collectAllIds(node: FilterNode, visited: Set<FilterNode> = new Set()): string[] {
    if (visited.has(node)) return [];
    visited.add(node);
    const out: string[] = [];
    if (node.spec.id) out.push(node.spec.id);
    node.children.forEach(c => out.push(...this.collectAllIds(c, visited)));
    return out;
  }

  private collectLeafIds(node: FilterNode, visited: Set<FilterNode> = new Set()): string[] {
    if (visited.has(node)) return [];
    visited.add(node);
    if (node.children.length === 0) {
      return node.spec.id ? [node.spec.id] : [];
    }
    const out: string[] = [];
    node.children.forEach(c => out.push(...this.collectLeafIds(c, visited)));
    return out;
  }

  get displayChips(): { id: string; name: string }[] {
    const result: { id: string; name: string }[] = [];
    const seen = new Set<string>();

    this.tickedIds.forEach(tickedId => {
      const node = this.nodesById.get(tickedId);
      if (!node) return;

      const leafIds = node.children.length === 0
        ? (node.spec.id ? [node.spec.id] : [])
        : this.collectLeafIds(node);

      leafIds.forEach(leafId => {
        if (seen.has(leafId)) return;
        seen.add(leafId);
        const leafNode = this.nodesById.get(leafId);
        if (leafNode?.spec.name) {
          result.push({ id: leafId, name: leafNode.spec.name });
        }
      });
    });

    return result;
  }

  get hasOptions(): boolean {
    return this.rootNodes.length > 0;
  }

  get tickedCount(): number {
    return this.tickedIds.size;
  }

  get triggerLabel(): string {
    if (!this.productTypeId) return 'Specifications';
    const count = this.tickedIds.size;
    if (count === 0) return 'Filter by Specification';
    return count === 1 ? '1 specification selected' : `${count} specifications selected`;
  }

  trackByNodeId(_: number, node: FilterNode) {
    return node.spec.id;
  }

  trackByChipId(_: number, chip: { id: string }) {
    return chip.id;
  }
}
